import React, { FC } from "react";

import {
  basketActiveAtom,
  basketSelector,
  basketTotalCountSelector,
  DataViewKey,
  dataViewPage,
  KpkBasket,
  SentryBreadcrumbCategory,
  useBasketList,
  useBasketPageCount,
} from "@keepeek/commons";
import { BasketLayout, withSuspense } from "@keepeek/refront-components";
import { LinearProgress } from "@mui/material";
import * as Sentry from "@sentry/nextjs";
import { useRouter } from "next/router";
import { useRecoilState, useRecoilValue } from "recoil";

import { KIcon } from "../../../components/common/KIcon";
import { currentBasketIdState } from "../../../providers/basket/atoms";
import { configSectionComponentBasketSelector } from "../../../providers/config/selectors";
import { PagesPath } from "../../App/utils";
import BasketActions from "../BasketActions";
import BasketBasicLoading from "../BasketBasicLoading";
import BasketElements from "../BasketElements";
import BasketError from "../BasketError";

/**
 * Loading component with total count and paginate
 */
const BasketAdvancedLoading: FC = function () {
  const router = useRouter();
  const [currentBasketId, setCurrentBasketId] = useRecoilState(currentBasketIdState);
  const currentBasket = useRecoilValue(basketSelector({ id: currentBasketId }));
  const { activateBasketPartitioning } = useRecoilValue(configSectionComponentBasketSelector) ?? {
    activateBasketPartitioning: false,
  };
  const { basketList } = useBasketList({
    basketPartitioning: activateBasketPartitioning,
  });
  const activeBasket = useRecoilValue(basketActiveAtom);
  const totalCount = useRecoilValue(basketTotalCountSelector(currentBasketId));
  const [page, setPage] = useRecoilState(dataViewPage(DataViewKey.BASKET));
  const sentryCallback = ({ category, message }) => Sentry.addBreadcrumb({ category, message });

  const selectCurrentBasketId = (basketId: KpkBasket["id"]) => {
    setCurrentBasketId(basketId);

    Sentry.addBreadcrumb({
      category: SentryBreadcrumbCategory.USER_INTERACTION,
      message: `selectCurrentBasketId: ${basketId}`,
    });
  };
  const pageCount = useBasketPageCount(currentBasket?.id);

  return (
    <BasketLayout
      basketList={basketList}
      elementCount={totalCount}
      pageCount={pageCount}
      currentPage={page}
      active={activeBasket}
      handleBasketSelect={selectCurrentBasketId}
      handlePageChange={setPage}
      handleBasketsRouting={() => router.push(PagesPath.BASKETS)}
      currentBasket={currentBasket}
      actions={<BasketActions sentryCallback={sentryCallback} />}
      KIcon={KIcon}
    >
      <LinearProgress />
      <BasketElements sentryCallback={sentryCallback} />
    </BasketLayout>
  );
};
export default withSuspense(BasketAdvancedLoading, {
  CustomErrorComponent: BasketError,
  LoadingComponent: BasketBasicLoading,
});
